import { useReducer, useMemo, useEffect } from 'react';
import { log } from '../utils/logger';

/**
 * Action Types for the App State
 */
export const ActionTypes = {
  // Authentication Actions
  SET_CURRENT_USER: 'SET_CURRENT_USER',
  UPDATE_CURRENT_USER: 'UPDATE_CURRENT_USER',
  LOGOUT_USER: 'LOGOUT_USER',
  SET_AUTH_LOADING: 'SET_AUTH_LOADING',
  SET_AUTH_ERROR: 'SET_AUTH_ERROR',
  
  // View/Navigation Actions
  SET_CURRENT_VIEW: 'SET_CURRENT_VIEW',
  SET_ACTIVE_TAB: 'SET_ACTIVE_TAB',
  SET_MOBILE_MENU_OPEN: 'SET_MOBILE_MENU_OPEN',
  SET_IS_DESKTOP: 'SET_IS_DESKTOP',
  
  // Reservations Actions
  SET_RESERVATIONS: 'SET_RESERVATIONS',
  ADD_RESERVATION: 'ADD_RESERVATION',
  UPDATE_RESERVATION: 'UPDATE_RESERVATION',
  DELETE_RESERVATION: 'DELETE_RESERVATION',
  SET_RESERVATION_LOADING: 'SET_RESERVATION_LOADING',
  SET_RESERVATION_ERROR: 'SET_RESERVATION_ERROR',
  SET_RESERVATION_SUCCESS: 'SET_RESERVATION_SUCCESS',
  CLEAR_RESERVATION_SUCCESS: 'CLEAR_RESERVATION_SUCCESS',
  
  // Venues Actions
  SET_VENUES: 'SET_VENUES',
  ADD_VENUE: 'ADD_VENUE',
  UPDATE_VENUE: 'UPDATE_VENUE',
  DELETE_VENUE: 'DELETE_VENUE',
  SET_VENUES_LOADING: 'SET_VENUES_LOADING',
  SET_VENUES_ERROR: 'SET_VENUES_ERROR',
  
  // Teams Actions
  SET_TEAMS: 'SET_TEAMS',
  ADD_TEAM: 'ADD_TEAM',
  UPDATE_TEAM: 'UPDATE_TEAM',
  DELETE_TEAM: 'DELETE_TEAM',
  SET_TEAMS_LOADING: 'SET_TEAMS_LOADING',
  SET_TEAMS_ERROR: 'SET_TEAMS_ERROR',
  
  // Tournaments Actions
  SET_TOURNAMENTS: 'SET_TOURNAMENTS',
  SET_SELECTED_TOURNAMENT: 'SET_SELECTED_TOURNAMENT',
  SET_TOURNAMENT_TEAMS: 'SET_TOURNAMENT_TEAMS',
  SET_TOURNAMENTS_LOADING: 'SET_TOURNAMENTS_LOADING',
  SET_TOURNAMENTS_ERROR: 'SET_TOURNAMENTS_ERROR',
  
  // Games Actions
  SET_GAMES: 'SET_GAMES',
  ADD_GAME: 'ADD_GAME',
  UPDATE_GAME: 'UPDATE_GAME',
  DELETE_GAME: 'DELETE_GAME',
  SET_GAMES_LOADING: 'SET_GAMES_LOADING',
  SET_GAMES_ERROR: 'SET_GAMES_ERROR',
  
  // Hotel Management Actions
  SET_HOTEL_CONFIRMATION: 'SET_HOTEL_CONFIRMATION',
  CLEAR_HOTEL_CONFIRMATION: 'CLEAR_HOTEL_CONFIRMATION',
  SET_HOTEL_LOADING: 'SET_HOTEL_LOADING',
  SET_HOTEL_ERROR: 'SET_HOTEL_ERROR',
  
  // UI State Actions
  SET_SIDEBAR_OPEN: 'SET_SIDEBAR_OPEN',
  SET_NOTIFICATION: 'SET_NOTIFICATION',
  CLEAR_NOTIFICATION: 'CLEAR_NOTIFICATION',
  SET_MODAL: 'SET_MODAL',
  CLOSE_MODAL: 'CLOSE_MODAL',
  
  // Batch Actions
  BATCH_UPDATE: 'BATCH_UPDATE',
  RESET_STATE: 'RESET_STATE',
};

/**
 * Initial State Structure
 */
const initialState = {
  // Authentication State
  auth: {
    currentUser: null,
    loading: true,
    error: null,
    initialized: false,
  },
  
  // Navigation State
  navigation: {
    currentView: 'login',
    activeTab: 'overview',
    mobileMenuOpen: false,
    sidebarOpen: true,
  },
  
  // Reservations State
  reservations: {
    data: [],
    loading: false,
    error: null,
    success: null,
    filters: {
      status: '',
      venue: '',
      dateRange: null,
      searchTerm: '',
    },
  },
  
  // Venues State
  venues: {
    data: [],
    loading: false,
    error: null,
    selectedVenue: null,
  },
  
  // Teams State
  teams: {
    data: [],
    loading: false,
    error: null,
    userTeams: [],
    selectedTeam: null,
  },
  
  // Tournaments State
  tournaments: {
    data: [],
    loading: false,
    error: null,
    selectedTournament: null,
    tournamentTeams: [],
  },
  
  // Games State
  games: {
    data: [],
    loading: false,
    error: null,
    selectedGame: null,
  },
  
  // Hotel Management State
  hotel: {
    confirmation: null,
    loading: false,
    error: null,
  },
  
  // UI State
  ui: {
    notification: null,
    modal: null,
    sidebarOpen: true,
  },
};

/**
 * Main Reducer Function
 */
const appReducer = (state, action) => {
  switch (action.type) {
    // Authentication Actions
    case ActionTypes.SET_CURRENT_USER:
      return {
        ...state,
        auth: {
          ...state.auth,
          currentUser: action.payload,
          loading: false,
          initialized: true,
        },
      };
      
    case ActionTypes.UPDATE_CURRENT_USER:
      return {
        ...state,
        auth: {
          ...state.auth,
          currentUser: {
            ...state.auth.currentUser,
            ...action.payload,
          },
        },
      };
      
    case ActionTypes.LOGOUT_USER:
      return {
        ...state,
        auth: {
          ...state.auth,
          currentUser: null,
          initialized: false,
        },
        navigation: {
          ...state.navigation,
          currentView: 'login',
        },
      };
      
    case ActionTypes.SET_AUTH_LOADING:
      return {
        ...state,
        auth: {
          ...state.auth,
          loading: action.payload,
        },
      };
      
    case ActionTypes.SET_AUTH_ERROR:
      return {
        ...state,
        auth: {
          ...state.auth,
          error: action.payload,
          loading: false,
        },
      };
    
    // View/Navigation Actions
    case ActionTypes.SET_CURRENT_VIEW:
      return {
        ...state,
        navigation: {
          ...state.navigation,
          currentView: action.payload,
        },
      };
      
    case ActionTypes.SET_ACTIVE_TAB:
      return {
        ...state,
        navigation: {
          ...state.navigation,
          activeTab: action.payload,
        },
      };
      
    case ActionTypes.SET_MOBILE_MENU_OPEN:
      return {
        ...state,
        ui: {
          ...state.ui,
          mobileMenuOpen: action.payload,
        },
      };
    
    case ActionTypes.SET_IS_DESKTOP:
      return {
        ...state,
        ui: {
          ...state.ui,
          isDesktop: action.payload,
        },
      };
    
    // Reservations Actions
    case ActionTypes.SET_RESERVATIONS:
      return {
        ...state,
        reservations: {
          ...state.reservations,
          data: action.payload,
          loading: false,
        },
      };
      
    case ActionTypes.ADD_RESERVATION:
      return {
        ...state,
        reservations: {
          ...state.reservations,
          data: [...state.reservations.data, action.payload],
        },
      };
      
    case ActionTypes.UPDATE_RESERVATION:
      return {
        ...state,
        reservations: {
          ...state.reservations,
          data: state.reservations.data.map(r => 
            r.id === action.payload.id ? action.payload : r
          ),
        },
      };
      
    case ActionTypes.DELETE_RESERVATION:
      return {
        ...state,
        reservations: {
          ...state.reservations,
          data: state.reservations.data.filter(r => r.id !== action.payload),
        },
      };
      
    case ActionTypes.SET_RESERVATION_LOADING:
      return {
        ...state,
        reservations: {
          ...state.reservations,
          loading: action.payload,
        },
      };
      
    case ActionTypes.SET_RESERVATION_ERROR:
      return {
        ...state,
        reservations: {
          ...state.reservations,
          error: action.payload,
          loading: false,
        },
      };
      
    case ActionTypes.SET_RESERVATION_SUCCESS:
      return {
        ...state,
        reservations: {
          ...state.reservations,
          success: action.payload,
        },
      };
      
    case ActionTypes.CLEAR_RESERVATION_SUCCESS:
      return {
        ...state,
        reservations: {
          ...state.reservations,
          success: null,
        },
      };
    
    // Venues Actions
    case ActionTypes.SET_VENUES:
      return {
        ...state,
        venues: {
          ...state.venues,
          data: action.payload,
          loading: false,
        },
      };
      
    case ActionTypes.ADD_VENUE:
      return {
        ...state,
        venues: {
          ...state.venues,
          data: [...state.venues.data, action.payload],
        },
      };
      
    case ActionTypes.UPDATE_VENUE:
      return {
        ...state,
        venues: {
          ...state.venues,
          data: state.venues.data.map(v => 
            v.id === action.payload.id ? action.payload : v
          ),
        },
      };
      
    case ActionTypes.DELETE_VENUE:
      return {
        ...state,
        venues: {
          ...state.venues,
          data: state.venues.data.filter(v => v.id !== action.payload),
        },
      };
      
    case ActionTypes.SET_VENUES_LOADING:
      return {
        ...state,
        venues: {
          ...state.venues,
          loading: action.payload,
        },
      };
      
    case ActionTypes.SET_VENUES_ERROR:
      return {
        ...state,
        venues: {
          ...state.venues,
          error: action.payload,
          loading: false,
        },
      };
    
    // Teams Actions
    case ActionTypes.SET_TEAMS:
      return {
        ...state,
        teams: {
          ...state.teams,
          data: action.payload,
          loading: false,
        },
      };
      
    case ActionTypes.ADD_TEAM:
      return {
        ...state,
        teams: {
          ...state.teams,
          data: [...state.teams.data, action.payload],
        },
      };
      
    case ActionTypes.UPDATE_TEAM:
      return {
        ...state,
        teams: {
          ...state.teams,
          data: state.teams.data.map(t => 
            t.id === action.payload.id ? action.payload : t
          ),
        },
      };
      
    case ActionTypes.DELETE_TEAM:
      return {
        ...state,
        teams: {
          ...state.teams,
          data: state.teams.data.filter(t => t.id !== action.payload),
        },
      };
    
    // Tournaments Actions
    case ActionTypes.SET_TOURNAMENTS:
      return {
        ...state,
        tournaments: {
          ...state.tournaments,
          data: action.payload,
          loading: false,
        },
      };
      
    case ActionTypes.SET_SELECTED_TOURNAMENT:
      return {
        ...state,
        tournaments: {
          ...state.tournaments,
          selectedTournament: action.payload,
        },
      };
      
    case ActionTypes.SET_TOURNAMENT_TEAMS:
      return {
        ...state,
        tournaments: {
          ...state.tournaments,
          tournamentTeams: action.payload,
        },
      };
    
    // Games Actions
    case ActionTypes.SET_GAMES:
      return {
        ...state,
        games: {
          ...state.games,
          data: action.payload,
          loading: false,
        },
      };
      
    case ActionTypes.ADD_GAME:
      return {
        ...state,
        games: {
          ...state.games,
          data: [...state.games.data, action.payload],
        },
      };
      
    case ActionTypes.UPDATE_GAME:
      return {
        ...state,
        games: {
          ...state.games,
          data: state.games.data.map(g => 
            g.id === action.payload.id ? action.payload : g
          ),
        },
      };
    
    // Hotel Management Actions
    case ActionTypes.SET_HOTEL_CONFIRMATION:
      return {
        ...state,
        hotel: {
          ...state.hotel,
          confirmation: action.payload,
          loading: false,
        },
      };
      
    case ActionTypes.CLEAR_HOTEL_CONFIRMATION:
      return {
        ...state,
        hotel: {
          ...state.hotel,
          confirmation: null,
        },
      };
    
    // UI State Actions
    case ActionTypes.SET_SIDEBAR_OPEN:
      return {
        ...state,
        ui: {
          ...state.ui,
          sidebarOpen: action.payload,
        },
      };
      
    case ActionTypes.SET_NOTIFICATION:
      return {
        ...state,
        ui: {
          ...state.ui,
          notification: action.payload,
        },
      };
      
    case ActionTypes.CLEAR_NOTIFICATION:
      return {
        ...state,
        ui: {
          ...state.ui,
          notification: null,
        },
      };
      
    case ActionTypes.SET_MODAL:
      return {
        ...state,
        ui: {
          ...state.ui,
          modal: action.payload,
        },
      };
      
    case ActionTypes.CLOSE_MODAL:
      return {
        ...state,
        ui: {
          ...state.ui,
          modal: null,
        },
      };
    
    // Batch Actions
    case ActionTypes.BATCH_UPDATE:
      return {
        ...state,
        ...action.payload,
      };
      
    case ActionTypes.RESET_STATE:
      return initialState;
    
    default:
      return state;
  }
};

/**
 * Custom Hook for App State Management
 */
export const useAppState = () => {
  const [state, dispatch] = useReducer(appReducer, initialState);
  
  // Action Creators
  const actions = useMemo(() => ({
    // Authentication Actions
    setCurrentUser: (user) => dispatch({ type: ActionTypes.SET_CURRENT_USER, payload: user }),
    updateCurrentUser: (updates) => dispatch({ type: ActionTypes.UPDATE_CURRENT_USER, payload: updates }),
    logoutUser: () => dispatch({ type: ActionTypes.LOGOUT_USER }),
    setAuthLoading: (loading) => dispatch({ type: ActionTypes.SET_AUTH_LOADING, payload: loading }),
    setAuthError: (error) => dispatch({ type: ActionTypes.SET_AUTH_ERROR, payload: error }),
    
    // Navigation Actions
    setCurrentView: (view) => dispatch({ type: ActionTypes.SET_CURRENT_VIEW, payload: view }),
    setActiveTab: (tab) => dispatch({ type: ActionTypes.SET_ACTIVE_TAB, payload: tab }),
    setMobileMenuOpen: (open) => dispatch({ type: ActionTypes.SET_MOBILE_MENU_OPEN, payload: open }),
    setIsDesktop: (isDesktop) => dispatch({ type: ActionTypes.SET_IS_DESKTOP, payload: isDesktop }),
    
    // Reservations Actions
    setReservations: (reservations) => dispatch({ type: ActionTypes.SET_RESERVATIONS, payload: reservations }),
    addReservation: (reservation) => dispatch({ type: ActionTypes.ADD_RESERVATION, payload: reservation }),
    updateReservation: (reservation) => dispatch({ type: ActionTypes.UPDATE_RESERVATION, payload: reservation }),
    deleteReservation: (id) => dispatch({ type: ActionTypes.DELETE_RESERVATION, payload: id }),
    setReservationLoading: (loading) => dispatch({ type: ActionTypes.SET_RESERVATION_LOADING, payload: loading }),
    setReservationError: (error) => dispatch({ type: ActionTypes.SET_RESERVATION_ERROR, payload: error }),
    setReservationSuccess: (success) => dispatch({ type: ActionTypes.SET_RESERVATION_SUCCESS, payload: success }),
    clearReservationSuccess: () => dispatch({ type: ActionTypes.CLEAR_RESERVATION_SUCCESS }),
    
    // Venues Actions
    setVenues: (venues) => dispatch({ type: ActionTypes.SET_VENUES, payload: venues }),
    addVenue: (venue) => dispatch({ type: ActionTypes.ADD_VENUE, payload: venue }),
    updateVenue: (venue) => dispatch({ type: ActionTypes.UPDATE_VENUE, payload: venue }),
    deleteVenue: (id) => dispatch({ type: ActionTypes.DELETE_VENUE, payload: id }),
    setVenuesLoading: (loading) => dispatch({ type: ActionTypes.SET_VENUES_LOADING, payload: loading }),
    setVenuesError: (error) => dispatch({ type: ActionTypes.SET_VENUES_ERROR, payload: error }),
    
    // Teams Actions
    setTeams: (teams) => dispatch({ type: ActionTypes.SET_TEAMS, payload: teams }),
    addTeam: (team) => dispatch({ type: ActionTypes.ADD_TEAM, payload: team }),
    updateTeam: (team) => dispatch({ type: ActionTypes.UPDATE_TEAM, payload: team }),
    deleteTeam: (id) => dispatch({ type: ActionTypes.DELETE_TEAM, payload: id }),
    
    // Tournaments Actions
    setTournaments: (tournaments) => dispatch({ type: ActionTypes.SET_TOURNAMENTS, payload: tournaments }),
    setSelectedTournament: (tournament) => dispatch({ type: ActionTypes.SET_SELECTED_TOURNAMENT, payload: tournament }),
    setTournamentTeams: (teams) => dispatch({ type: ActionTypes.SET_TOURNAMENT_TEAMS, payload: teams }),
    
    // Games Actions
    setGames: (games) => dispatch({ type: ActionTypes.SET_GAMES, payload: games }),
    addGame: (game) => dispatch({ type: ActionTypes.ADD_GAME, payload: game }),
    updateGame: (game) => dispatch({ type: ActionTypes.UPDATE_GAME, payload: game }),
    
    // Hotel Actions
    setHotelConfirmation: (confirmation) => dispatch({ type: ActionTypes.SET_HOTEL_CONFIRMATION, payload: confirmation }),
    clearHotelConfirmation: () => dispatch({ type: ActionTypes.CLEAR_HOTEL_CONFIRMATION }),
    
    // UI Actions
    setSidebarOpen: (open) => dispatch({ type: ActionTypes.SET_SIDEBAR_OPEN, payload: open }),
    setNotification: (notification) => dispatch({ type: ActionTypes.SET_NOTIFICATION, payload: notification }),
    clearNotification: () => dispatch({ type: ActionTypes.CLEAR_NOTIFICATION }),
    setModal: (modal) => dispatch({ type: ActionTypes.SET_MODAL, payload: modal }),
    closeModal: () => dispatch({ type: ActionTypes.CLOSE_MODAL }),
    
    // Batch Actions
    batchUpdate: (updates) => dispatch({ type: ActionTypes.BATCH_UPDATE, payload: updates }),
    resetState: () => dispatch({ type: ActionTypes.RESET_STATE }),
  }), []);
  
  // Selectors for common state queries
  const selectors = useMemo(() => ({
    // Auth selectors
    isAuthenticated: () => !!state.auth.currentUser,
    isAdmin: () => state.auth.currentUser?.role === 'admin',
    isVenueManager: () => state.auth.currentUser?.role === 'venue_manager',
    isTeamManager: () => state.auth.currentUser?.role === 'team_manager',
    isEmployee: () => state.auth.currentUser?.role === 'teamrezy_employee',
    
    // Reservation selectors
    getReservations: () => state.reservations.data,
    getReservationById: (id) => state.reservations.data.find(r => r.id === id),
    getReservationsByStatus: (status) => state.reservations.data.filter(r => r.status === status),
    getReservationsByVenue: (venueId) => state.reservations.data.filter(r => r.venue_id === venueId),
    
    // Venue selectors
    getVenues: () => state.venues.data,
    getVenueById: (id) => state.venues.data.find(v => v.id === id),
    getActiveVenues: () => state.venues.data.filter(v => v.is_active),
    
    // Team selectors
    getTeamById: (id) => state.teams.data.find(t => t.id === id),
    getUserTeams: () => state.teams.userTeams,
    
    // Tournament selectors
    getTournamentById: (id) => state.tournaments.data.find(t => t.id === id),
    getActiveTournaments: () => state.tournaments.data.filter(t => {
      const now = new Date();
      return new Date(t.start_date) <= now && new Date(t.end_date) >= now;
    }),
    
    // Game selectors
    getGameById: (id) => state.games.data.find(g => g.id === id),
    getUpcomingGames: () => state.games.data.filter(g => new Date(g.starts_at) > new Date()),
  }), [state]);
  
  // Persist certain state to localStorage
  useEffect(() => {
    const persistedState = {
      activeTab: state.navigation.activeTab,
      sidebarOpen: state.ui.sidebarOpen,
    };
    localStorage.setItem('appState', JSON.stringify(persistedState));
  }, [state.navigation.activeTab, state.ui.sidebarOpen]);
  
  // Load persisted state on mount
  useEffect(() => {
    const loadPersistedState = () => {
      try {
        // Check if there's a hash in the URL to override the active tab (e.g., password recovery)
        const hash = window.location.hash.substring(1); // Remove the # symbol
        log.info('🔍 useAppState: Current hash:', hash || '(empty)');

        // Check if hash is a simple tab name
        let hashTab = ['overview', 'reservations', 'teams', 'venues', 'account', 'admin'].includes(hash) ? hash : null;

        // If not a simple tab name, check if it contains password recovery parameters
        if (!hashTab && hash.includes('type=recovery')) {
          log.info('🔍 useAppState: Detected password recovery flow in hash - directing to account tab');
          hashTab = 'account';
        }

        log.info('🔍 useAppState: Parsed hashTab:', hashTab || '(no valid tab in hash)');

        const saved = localStorage.getItem('appState');
        const parsed = saved ? JSON.parse(saved) : {};
        log.info('🔍 useAppState: Saved activeTab:', parsed.activeTab || '(none)');

        const finalTab = hashTab || parsed.activeTab || 'overview';
        log.info('🔍 useAppState: Final activeTab will be:', finalTab);

        // Always dispatch to set active tab, whether from hash, saved state, or default
        dispatch({
          type: ActionTypes.BATCH_UPDATE,
          payload: {
            navigation: {
              activeTab: finalTab,
              breadcrumb: [],
              history: []
            },
            ui: {
              sidebarOpen: parsed.sidebarOpen !== undefined ? parsed.sidebarOpen : true,
              mobileMenuOpen: false,
              isDesktop: window.innerWidth >= 1024,
            },
          },
        });

        // Clear the hash after reading it to avoid it interfering with future navigation
        if (hashTab) {
          log.info('🔍 useAppState: Detected hash tab override:', hashTab, '- clearing hash from URL');
          window.history.replaceState(null, '', window.location.pathname + window.location.search);
        }
      } catch (error) {
        log.error('Error loading persisted state:', error);
      }
    };

    loadPersistedState();
  }, [dispatch]); // Include dispatch dependency
  
  return {
    state,
    dispatch,
    actions,
    selectors,
  };
};

export default useAppState;